import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  format,
  isSameMonth,
  isToday,
} from "date-fns";
import { supabase } from "../../lib/supabase";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { extractAndFormatTime } from "../../lib/timeUtils";
import type { Deal } from "../../lib/types";
import DealLink from "../DealLink";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";
import { addDaysISO, buildDaysIn, BUILD_SPAN_LOOKBACK_DAYS } from "../../lib/buildCalendar";

/* ── types ─────────────────────────────────────────────── */
type EventType = "adj" | "reinspAdj" | "pre" | "build";

interface CalEvent {
  deal: Deal;
  type: EventType;
  time: string | null; // formatted display time
  mine: boolean; // assigned to the board owner (drives the YOU badge + highlight)
}

const COLOR: Record<EventType, { bg: string; text: string; dot: string; label: string }> = {
  adj:       { bg: "bg-indigo-100",  text: "text-indigo-800",  dot: "bg-indigo-500",  label: "Adjuster" },
  reinspAdj: { bg: "bg-purple-100",  text: "text-purple-800",  dot: "bg-purple-500",  label: "2nd Adjuster" },
  pre:       { bg: "bg-amber-100",   text: "text-amber-800",   dot: "bg-amber-500",   label: "Pre-Inspection" },
  build:     { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500", label: "Build" },
};

/* ── component ─────────────────────────────────────────── */
export default function CalendarTab({ highlightRsId }: { highlightRsId?: string } = {}) {
  const now = useMemo(() => new Date(), []);
  const [events, setEvents] = useState<Map<string, CalEvent[]> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(format(now, "yyyy-MM-dd"));
  const [reloadKey, setReloadKey] = useState(0);

  const prevMonth = subMonths(now, 1);
  const nextMonth = addMonths(now, 1);

  /* Date range: start of prev month week → end of next month week */
  const rangeStart = format(startOfMonth(prevMonth), "yyyy-MM-dd");
  const rangeEnd = format(endOfMonth(nextMonth), "yyyy-MM-dd");

  // Realtime — when any deal changes, re-fetch. Same wiring the dispatch
  // ScheduleTab uses, so the two calendars never disagree about what's on
  // the book.
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  useRealtimeTable("deals", reload);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const cols =
        "id,homeowner_name,property_street,property_city,property_state,adjuster_meeting_date,pre_inspection_date,pre_inspection_assigned_to,assigned_rs_id,build_scheduled_date,build_end_date,reinspection_adjuster_meeting_date,owner_id,claim_number,stage,insurance_company";

      const startTS = `${rangeStart}T00:00:00Z`;
      const endTS = `${rangeEnd}T23:59:59Z`;

      const [
        { data: adj, error: e1 },
        { data: pre, error: e2 },
        { data: builds, error: e3 },
        { data: reinspAdj, error: e4 },
      ] = await Promise.all([
        supabase.from("deals").select(cols).gte("adjuster_meeting_date", startTS).lte("adjuster_meeting_date", endTS),
        supabase.from("deals").select(cols).gte("pre_inspection_date", startTS).lte("pre_inspection_date", endTS),
        // Builds that started earlier still show on each later day they run (Oct 26, 2026).
        supabase.from("deals").select(cols).gte("build_scheduled_date", addDaysISO(rangeStart, -BUILD_SPAN_LOOKBACK_DAYS)).lte("build_scheduled_date", rangeEnd),
        supabase.from("deals").select(cols).gte("reinspection_adjuster_meeting_date", startTS).lte("reinspection_adjuster_meeting_date", endTS),
      ]);

      const err = e1 ?? e2 ?? e3 ?? e4;
      if (err) { if (!cancelled) setError(err.message); return; }

      const map = new Map<string, CalEvent[]>();
      const push = (iso: string, ev: CalEvent) => {
        const arr = map.get(iso) ?? [];
        arr.push(ev);
        map.set(iso, arr);
      };

      // "Mine" = assigned to the board owner. Adjuster / 2nd-adjuster / build
      // key off assigned_rs_id (the RS on the meeting); pre-inspections can also
      // match on pre_inspection_assigned_to. Mirrors TodayTab.stopIsMine so the
      // two views agree on what's yours.
      const isMine = (d: Deal, type: EventType): boolean =>
        !!highlightRsId &&
        ((d.assigned_rs_id ?? null) === highlightRsId ||
          (type === "pre" && (d.pre_inspection_assigned_to ?? null) === highlightRsId));

      for (const d of (adj ?? []) as Deal[]) {
        const iso = d.adjuster_meeting_date?.slice(0, 10);
        if (iso) push(iso, { deal: d, type: "adj", time: extractAndFormatTime(d.adjuster_meeting_date), mine: isMine(d, "adj") });
      }
      for (const d of (reinspAdj ?? []) as Deal[]) {
        const iso = d.reinspection_adjuster_meeting_date?.slice(0, 10);
        if (iso) push(iso, { deal: d, type: "reinspAdj", time: extractAndFormatTime(d.reinspection_adjuster_meeting_date), mine: isMine(d, "reinspAdj") });
      }
      for (const d of (pre ?? []) as Deal[]) {
        const iso = d.pre_inspection_date?.slice(0, 10);
        if (iso) push(iso, { deal: d, type: "pre", time: extractAndFormatTime(d.pre_inspection_date), mine: isMine(d, "pre") });
      }
      for (const d of (builds ?? []) as Deal[]) {
        // A multi-day build sits on each of its days with "Day n of N".
        for (const bd of buildDaysIn(d.build_scheduled_date, d.build_end_date, rangeStart, rangeEnd)) {
          push(bd.iso, { deal: d, type: "build", time: bd.of > 1 ? `Day ${bd.day} of ${bd.of}` : null, mine: isMine(d, "build") });
        }
      }

      // Sort events within each day by time
      for (const [, arr] of map) {
        arr.sort((a, b) => (a.time ?? "ZZ").localeCompare(b.time ?? "ZZ"));
      }

      if (!cancelled) setEvents(map);
    }
    load();
    return () => { cancelled = true; };
  }, [rangeStart, rangeEnd, reloadKey, highlightRsId]);

  if (error) return <div className="bg-red-50 text-red-700 p-4 rounded">Couldn't load calendar — {error}</div>;
  if (!events) return <div className="text-slate-500 p-4">Loading calendar…</div>;

  const selectedEvents = events.get(selectedDate) ?? [];
  const selectedMineCount = highlightRsId ? selectedEvents.filter((e) => e.mine).length : 0;

  // Days (in the visible range) that have at least one event assigned to the
  // board owner — drives the teal ring on the mini calendars so they can scan
  // the month for their own stops.
  const mineDates = new Set<string>();
  if (highlightRsId) {
    for (const [iso, arr] of events) {
      if (arr.some((e) => e.mine)) mineDates.add(iso);
    }
  }

  return (
    <div>
      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4 text-xs">
        {(Object.keys(COLOR) as EventType[]).map((t) => (
          <span key={t} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-full ${COLOR[t].dot}`} />
            {COLOR[t].label}
          </span>
        ))}
      </div>

      {/* 3 month calendars */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <MonthGrid month={prevMonth} now={now} events={events} mineDates={mineDates} selectedDate={selectedDate} onSelect={setSelectedDate} />
        <MonthGrid month={now} now={now} events={events} mineDates={mineDates} selectedDate={selectedDate} onSelect={setSelectedDate} />
        <MonthGrid month={nextMonth} now={now} events={events} mineDates={mineDates} selectedDate={selectedDate} onSelect={setSelectedDate} />
      </div>

      {/* Selected day detail */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold text-bardin-ink mb-3">
          {format(new Date(selectedDate + "T12:00:00"), "EEEE, MMMM d, yyyy")}
          {isToday(new Date(selectedDate + "T12:00:00")) && (
            <span className="ml-2 text-xs bg-bardin-orange text-white px-2 py-0.5 rounded-full">Today</span>
          )}
          <span className="ml-2 text-sm font-normal text-slate-500">
            {selectedEvents.length} event{selectedEvents.length !== 1 ? "s" : ""}
          </span>
          {selectedMineCount > 0 && (
            <span className="ml-2 text-xs font-bold bg-bardin-teal/15 text-bardin-teal px-2 py-0.5 rounded-full">
              {selectedMineCount} yours
            </span>
          )}
        </h3>

        {selectedEvents.length === 0 ? (
          <p className="text-slate-400 text-sm italic">No events scheduled</p>
        ) : (
          <div className="space-y-2">
            {selectedEvents.map((ev, i) => {
              const c = COLOR[ev.type];
              return (
                <div
                  key={`${ev.deal.id}-${ev.type}-${i}`}
                  className={
                    `flex items-center gap-3 px-3 py-2 rounded-lg ${c.bg} ` +
                    (ev.mine ? "ring-2 ring-inset ring-bardin-teal" : "")
                  }
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${c.dot}`} />
                  <div className={`flex-1 min-w-0 ${c.text}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{c.label}</span>
                      {ev.time && <span className="text-xs opacity-75">{ev.time}</span>}
                      {ev.mine && (
                        <span className="text-[9px] font-extrabold tracking-wide bg-bardin-teal text-white px-1.5 py-0.5 rounded">
                          YOU
                        </span>
                      )}
                    </div>
                    <div className="text-sm truncate">
                      <DealLink id={ev.deal.id}>{ev.deal.homeowner_name}</DealLink>
                      {ev.deal.property_street && (
                        <span className="ml-1 opacity-75">
                          — {isAddressLinkable(ev.deal) ? (
                            <a
                              href={mapsHref(ev.deal)}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="hover:underline"
                            >
                              {ev.deal.property_street}
                            </a>
                          ) : (
                            ev.deal.property_street
                          )}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 text-[11px] opacity-75 mt-0.5">
                      {ev.deal.claim_number && <span>#{ev.deal.claim_number}</span>}
                      {ev.deal.insurance_company && <span>{ev.deal.insurance_company}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Mini month grid ────────────────────────────────────── */
function MonthGrid({
  month,
  now,
  events,
  mineDates,
  selectedDate,
  onSelect,
}: {
  month: Date;
  now: Date;
  events: Map<string, CalEvent[]>;
  mineDates: Set<string>;
  selectedDate: string;
  onSelect: (iso: string) => void;
}) {
  const mStart = startOfMonth(month);
  const mEnd = endOfMonth(month);
  const calStart = startOfWeek(mStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(mEnd, { weekStartsOn: 1 });
  const isCurrentMonth = isSameMonth(month, now);

  // Build weeks
  const weeks: Date[][] = [];
  let cursor = calStart;
  while (cursor <= calEnd) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(cursor);
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
  }

  return (
    <div className={`bg-white rounded-lg shadow p-3 ${isCurrentMonth ? "ring-2 ring-bardin-teal" : ""}`}>
      <div className="text-center font-semibold text-bardin-ink mb-2">
        {format(month, "MMMM yyyy")}
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 text-center text-[10px] text-slate-400 font-medium mb-1">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      {/* Day cells */}
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7">
          {week.map((day) => {
            const iso = format(day, "yyyy-MM-dd");
            const inMonth = isSameMonth(day, month);
            const today = isToday(day);
            const selected = iso === selectedDate;
            const dayEvents = events.get(iso) ?? [];
            const hasEvents = dayEvents.length > 0;
            const hasMine = mineDates.has(iso);

            // Count by type for dots
            const types = new Set(dayEvents.map((e) => e.type));

            return (
              <button
                key={iso}
                onClick={() => onSelect(iso)}
                title={hasMine ? "You have a stop this day" : undefined}
                className={
                  "relative flex flex-col items-center py-1 text-xs rounded transition-colors " +
                  (!inMonth ? "text-slate-300 " : "text-slate-700 ") +
                  (selected ? "bg-bardin-teal/15 font-bold " : "hover:bg-slate-100 ") +
                  (today ? "font-bold " : "") +
                  (hasMine && !selected ? "ring-1 ring-inset ring-bardin-teal/60 " : "")
                }
              >
                <span
                  className={
                    "w-6 h-6 flex items-center justify-center rounded-full " +
                    (today && !selected ? "bg-bardin-orange text-white " : "") +
                    (selected ? "bg-bardin-teal text-white " : "")
                  }
                >
                  {format(day, "d")}
                </span>

                {/* Event dots */}
                {hasEvents && (
                  <div className="flex gap-0.5 mt-0.5 h-2">
                    {(Array.from(types) as EventType[]).map((t) => (
                      <span key={t} className={`w-1.5 h-1.5 rounded-full ${COLOR[t].dot}`} />
                    ))}
                  </div>
                )}
                {!hasEvents && <div className="h-2 mt-0.5" />}

                {/* Event count badge for busy days */}
                {dayEvents.length > 3 && (
                  <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-bardin-orange text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                    {dayEvents.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
